import React from "react";
import { useState } from "react";
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import { Badge } from "@/components/ui/badge";
import { Button } from "@/components/ui/button";
import { useFavorites, IdeaStatus, FavoriteWithStatus } from "@/hooks/useFavorites";
import { useCreateProjectFromStartup } from "@/hooks/useCreateProjectFromStartup";
import { useToast } from "@/hooks/use-toast";
import { Link, useNavigate } from "react-router-dom";
import { normalizeError } from "@/lib/normalizeError";
import { 
  Heart, 
  Rocket, 
  Loader2, 
  BookmarkCheck,
  Hammer,
  PartyPopper,
  ArrowRight,
  ExternalLink
} from "lucide-react";
import { Skeleton } from "@/components/ui/skeleton";
import { Tabs, TabsContent, TabsList, TabsTrigger } from "@/components/ui/tabs";

const StatusBadge = ({ status }: { status: IdeaStatus }) => {
  const config = {
    saved: { 
      label: 'Saved', 
      icon: BookmarkCheck, 
      className: 'bg-muted text-muted-foreground border-muted-foreground/20'
    },
    in_progress: { 
      label: 'In Progress', 
      icon: Hammer, 
      className: 'bg-primary/10 text-primary border-primary/20'
    },
    shipped: { 
      label: 'Shipped', 
      icon: PartyPopper, 
      className: 'bg-green-500/10 text-green-600 border-green-500/20'
    },
  };

  const { label, icon: Icon, className } = config[status];

  return (
    <Badge variant="outline" className={`gap-1 ${className}`}>
      <Icon className="h-3 w-3" />
      {label}
    </Badge>
  );
};

interface IdeaCardProps {
  fav: FavoriteWithStatus;
  onRemove: (ideaId: string) => void;
  isToggling: boolean;
  onConvert: (ideaId: string) => void;
  isConverting: boolean;
  onContinueBuilding: (ideaId: string, projectId?: string) => void;
  isContinuing: boolean;
}

const IdeaCard = ({ 
  fav, 
  onRemove, 
  isToggling, 
  onConvert, 
  isConverting,
  onContinueBuilding,
  isContinuing
}: IdeaCardProps) => {
  const idea = fav.ideas;
  
  if (!idea) return null;

  const handleContinueBuilding = (e: React.MouseEvent) => {
    e.preventDefault();
    e.stopPropagation();
    console.log("=== CONTINUE BUILDING CLICKED ===");
    console.log("continue_click", { ideaId: idea.id, projectId: fav.project_id });
    console.log("Button element:", e.currentTarget);
    console.log("Event target:", e.target);
    onContinueBuilding(idea.id, fav.project_id);
  };

  const handleConvert = (e: React.MouseEvent) => {
    e.preventDefault();
    e.stopPropagation();
    onConvert(idea.id);
  };

  return (
    <Card className="hover:shadow-md transition-all border-border/50 group relative">
      <CardContent className="p-5 space-y-3">
        <div className="flex items-start justify-between gap-2">
          <Link to={`/idea/${idea.id}`} className="flex-1 min-w-0">
            <h3 className="font-semibold text-base line-clamp-1 group-hover:text-primary transition-colors">
              {idea.title}
            </h3>
          </Link>
          <div className="flex items-center gap-2 flex-shrink-0">
            <StatusBadge status={fav.status} />
            <Button
              variant="ghost"
              size="icon"
              type="button"
              className="h-7 w-7 opacity-0 group-hover:opacity-100 transition-opacity"
              onClick={(e) => {
                e.preventDefault();
                e.stopPropagation();
                onRemove(idea.id);
              }}
              disabled={isToggling}
            >
              <Heart className="h-3.5 w-3.5 fill-primary text-primary" />
            </Button>
          </div>
        </div>

        <p className="text-sm text-muted-foreground line-clamp-2">
          {idea.tagline || idea.description || 'No description'}
        </p>

        <div className="flex flex-wrap gap-1.5">
          {idea.niche && (
            <Badge variant="secondary" className="text-xs">
              {idea.niche}
            </Badge>
          )}
          {idea.tags?.slice(0, 2).map((tag) => (
            <Badge key={tag} variant="outline" className="text-xs">
              {tag}
            </Badge>
          ))}
        </div>

        <div className="flex items-center gap-2 pt-2 border-t border-border/50">
          {fav.status === 'saved' && (
            <Button 
              type="button"
              size="sm" 
              className="gap-1.5 flex-1"
              onClick={handleConvert}
              disabled={isConverting || !idea.id}
            >
              {isConverting ? (
                <>
                  <Loader2 className="h-3.5 w-3.5 animate-spin" />
                  Creating...
                </>
              ) : (
                <>
                  <Rocket className="h-3.5 w-3.5" />
                  Turn into Project
                </>
              )}
            </Button>
          )}
          
          {(fav.status === 'in_progress') && (
            <Button 
              type="button"
              size="sm" 
              className="gap-1.5 flex-1"
              onClick={handleContinueBuilding}
              disabled={isContinuing || !idea.id}
            >
              {isContinuing ? (
                <>
                  <Loader2 className="h-3.5 w-3.5 animate-spin" />
                  Loading...
                </>
              ) : (
                <>
                  <Hammer className="h-3.5 w-3.5" />
                  Continue Building
                  <ArrowRight className="h-3.5 w-3.5" />
                </>
              )}
            </Button>
          )}
          
          {fav.status === 'shipped' && (
            <Button 
              type="button"
              size="sm" 
              variant="outline"
              className="gap-1.5 flex-1"
              onClick={handleContinueBuilding}
              disabled={isContinuing || !idea.id}
            >
              {isContinuing ? (
                <>
                  <Loader2 className="h-3.5 w-3.5 animate-spin" />
                  Loading...
                </>
              ) : (
                <>
                  <ExternalLink className="h-3.5 w-3.5" />
                  View Project
                </>
              )}
            </Button>
          )}
        </div>
      </CardContent>
    </Card>
  );
};

export const SavedIdeas = () => {
  const navigate = useNavigate();
  const { toast } = useToast();
  const [isContinuing, setIsContinuing] = useState(false);
  const { 
    favorites, 
    savedIdeas, 
    inProgressIdeas, 
    shippedIdeas,
    isLoading, 
    toggleFavorite, 
    isToggling 
  } = useFavorites();
  const { createProjectFromIdea, isCreating } = useCreateProjectFromStartup();

  const handleRemove = (ideaId: string) => {
    toggleFavorite({ ideaId, isFavorited: true });
  };

  const handleConvert = async (ideaId: string) => {
    await createProjectFromIdea(ideaId);
  };

  const handleContinueBuilding = async (ideaId: string, projectId?: string) => {
    if (!ideaId) {
      toast({
        title: "Error",
        description: "Missing idea ID. Please try again.",
        variant: "destructive",
      });
      return;
    }

    setIsContinuing(true);
    
    try {
      // If we have a project_id, navigate directly (Continue Building — no auto-build)
      if (projectId) {
        console.log("Navigating to existing project:", projectId);
        navigate(`/code-workspace?projectId=${projectId}`, { state: { autoBuild: false } });
        return;
      }

      // No project_id - we need to create one first
      console.log("No project_id found, creating project for idea:", ideaId);
      toast({
        title: "Creating project...",
        description: "Setting up your workspace",
      });
      
      // This will create the project and navigate automatically
      await createProjectFromIdea(ideaId);
      
    } catch (err) {
      const e = normalizeError(err);
      console.error("Failed to continue building:", e.name, e.message, e.raw);
      toast({
        title: "Couldn't open builder",
        description: e.message,
        variant: "destructive",
      });
    } finally {
      setIsContinuing(false);
    }
  };

  if (isLoading) {
    return (
      <div className="grid gap-4 md:grid-cols-2 lg:grid-cols-3">
        {[1, 2, 3].map((i) => (
          <Card key={i}>
            <CardContent className="p-5">
              <Skeleton className="h-5 w-3/4 mb-2" />
              <Skeleton className="h-4 w-full mb-4" />
              <div className="flex gap-2 mb-4">
                <Skeleton className="h-5 w-16" />
                <Skeleton className="h-5 w-16" />
              </div>
              <Skeleton className="h-8 w-full" />
            </CardContent>
          </Card>
        ))}
      </div>
    );
  }

  if (!favorites || favorites.length === 0) {
    return (
      <div className="text-center py-12 text-muted-foreground">
        <Heart className="h-16 w-16 mx-auto mb-4 opacity-20" />
        <p className="font-medium text-lg">No saved ideas yet</p>
        <p className="text-sm mt-2 max-w-md mx-auto">
          Browse the <Link to="/startups" className="text-primary hover:underline">Ideas Explorer</Link> to discover 
          startup ideas, then save the ones you want to build.
        </p>
      </div>
    );
  }

  const cardProps = {
    onRemove: handleRemove,
    isToggling,
    onConvert: handleConvert,
    isConverting: isCreating,
    onContinueBuilding: handleContinueBuilding,
    isContinuing: isContinuing || isCreating,
  };

  return (
    <Tabs defaultValue="all" className="w-full">
      <TabsList className="mb-4">
        <TabsTrigger value="all">All ({favorites.length})</TabsTrigger>
        <TabsTrigger value="saved">Saved ({savedIdeas.length})</TabsTrigger>
        <TabsTrigger value="in_progress">In Progress ({inProgressIdeas.length})</TabsTrigger>
        <TabsTrigger value="shipped">Shipped ({shippedIdeas.length})</TabsTrigger>
      </TabsList>

      <TabsContent value="all">
        <div className="grid gap-4 md:grid-cols-2 lg:grid-cols-3">
          {favorites.map((fav) => (
            <IdeaCard key={fav.id} fav={fav} {...cardProps} />
          ))}
        </div>
      </TabsContent>

      <TabsContent value="saved">
        {savedIdeas.length === 0 ? (
          <p className="text-center py-6 text-muted-foreground">
            No saved ideas. All your ideas are being built or shipped!
          </p>
        ) : (
          <div className="grid gap-4 md:grid-cols-2 lg:grid-cols-3">
            {savedIdeas.map((fav) => (
              <IdeaCard key={fav.id} fav={fav} {...cardProps} />
            ))}
          </div>
        )}
      </TabsContent>

      <TabsContent value="in_progress">
        {inProgressIdeas.length === 0 ? (
          <p className="text-center py-6 text-muted-foreground">
            No ideas in progress. Pick a saved idea and start building!
          </p>
        ) : (
          <div className="grid gap-4 md:grid-cols-2 lg:grid-cols-3">
            {inProgressIdeas.map((fav) => (
              <IdeaCard key={fav.id} fav={fav} {...cardProps} />
            ))}
          </div>
        )}
      </TabsContent>

      <TabsContent value="shipped">
        {shippedIdeas.length === 0 ? (
          <p className="text-center py-6 text-muted-foreground">
            No shipped ideas yet. Keep building to ship your first project!
          </p>
        ) : (
          <div className="grid gap-4 md:grid-cols-2 lg:grid-cols-3">
            {shippedIdeas.map((fav) => (
              <IdeaCard key={fav.id} fav={fav} {...cardProps} />
            ))}
          </div>
        )}
      </TabsContent>
    </Tabs>
  );
};